import { useCallback, useState } from 'react';
import * as vp from 'vopay';

const useStorage = () => {
  const handlers = {
    useIQ11TokenStorage: wrapSessionStorage<string>(vp.LS_IQ11_TOKEN),
    useIQ11IsBrowser: () => useSessionStorage<boolean>(vp.LS_IQ11_IS_BROWSER),
    useIQ11TokenInfo: () => useSessionStorage<vp.TokenInfoResponse>(vp.LS_IQ11_TOKEN_INFO),
    useIQ11ForOnboarding: () => useSessionStorage<boolean>(vp.LS_IQ11_FOR_ONBOARDING),
    useBankInfo: () => useSessionStorage<vp.FinancialInstitutionInfo>(vp.LS_BANK_INFO),
    useMainBankInfo: () => useSessionStorage<vp.FinancialInstitutionInfo>(vp.LS_MAIN_BANK_INFO),
    useSubBanks: () => useSessionStorage<vp.FinancialInstitutionInfo[]>(vp.LS_SUB_BANK),
    useBankType: () => useSessionStorage<'online' | 'manual' | undefined>(vp.LS_BANK_TYPE),
    useAccountType: () => useSessionStorage<'personal' | 'business' | undefined>(vp.LS_ACCOUNT_TYPE),
    useBankAccountRail: () => useSessionStorage<vp.BankingPaymentRail | undefined>(vp.LS_BANK_ACCOUNT_RAIL),
    usePaylinkTokenStorage: wrapSessionStorage<string>(vp.LS_PAYLINK_TOKEN),
    usePadAgreementStorage: wrapSessionStorage<string>(vp.LS_PAD_INFO),
    usePaymentMethodStorage: wrapSessionStorage<string>(vp.LS_PAYMENT_METHOD),
    useAccountVerificationTokenStorage: wrapSessionStorage<string>(vp.LS_ACCOUNT_VERIFICATION_TOKEN),
    useAccessTokenStorage: () => useSessionStorage<string>(vp.LS_ACCESS_TOKEN),
    useProcessorStorage: () => useSessionStorage<string>(vp.LS_PROCESSOR),
    useAccessTokenExtraInfoStorage: () => useSessionStorage<vp.AccessTokenExtraInfo>(vp.LS_ACCESS_TOKEN_EXTRA_INFO),
    // useSessionStorage is not being reactive, for some reason
    useJWTStorage: () => ({
      getJWT: () => sessionStorage.getItem(vp.LS_USER_JWT),
      storeJWT: (val: string) => sessionStorage.setItem(vp.LS_USER_JWT, val),
      clearJWT: () => sessionStorage.removeItem(vp.LS_USER_JWT),
    }),
  };
  const [iQ11Token, storeIQ11Token, clearIQ11Token] = handlers.useIQ11TokenStorage();
  const [iQ11TokenInfo, storeIQ11TokenInfo, clearIQ11TokenInfo] = handlers.useIQ11TokenInfo();
  const [iQ11IsBrowser, storeIQ11IsBrowser, clearIQ11IsBrowser] = handlers.useIQ11IsBrowser();
  const [iQ11ForOnboarding, storeIQ11ForOnboarding, clearIQ11ForOnboarding] = handlers.useIQ11ForOnboarding();
  const [accountType, storeAccountType, clearAccountType] = handlers.useAccountType();
  const [bankInfo, storeBankInfo, clearBankInfo] = handlers.useBankInfo();
  const [mainBankInfo, storeMainBankInfo, clearMainBankInfo] = handlers.useMainBankInfo();

  const [bankType, storeBankType, clearBankType] = handlers.useBankType();
  const [subBanks, storeSubBanks, clearSubBanks] = handlers.useSubBanks();
  const [bankAccountRail, storeBankAccountRail, clearBankAccountRail] = handlers.useBankAccountRail();
  const [Token, storePaylinkToken, clearPaylinkToken] = handlers.usePaylinkTokenStorage();
  const [padInfo, storePadInfo, clearPadInfo] = handlers.usePadAgreementStorage();
  const [paymentMethod, storePaymentMethod, clearPaymentMethod] = handlers.usePaymentMethodStorage();
  const [accountVerificationToken, storeAccountVerificationToken, clearAccountVerificationToken] =
    handlers.useAccountVerificationTokenStorage();
  const [accessToken, storeAccessToken, clearAccessToken] = handlers.useAccessTokenStorage();
  const [processor, storeProcessor, clearProcessor] = handlers.useProcessorStorage();
  const [accessTokenExtraInfo, storeAccessTokenExtraInfo, clearAccessTokenExtraInfo] =
    handlers.useAccessTokenExtraInfoStorage();

  const JWTStorage = handlers.useJWTStorage();

  return {
    JWT: JWTStorage,
    token: {
      getIQ11Token: () => ({ iQ11Token, iQ11TokenInfo, iQ11IsBrowser, iQ11ForOnboarding }),
      storeIQ11TokenResponse: (response: vp.TokenInfoResponse) => {
        storeIQ11TokenInfo(response);
        JWTStorage.storeJWT(response.JWT);
      },

      storeIQ11Token: (TokenIQ11: string, IsBrowser: boolean, IQ11ForOnboarding: boolean) => {
        storeIQ11Token(TokenIQ11);
        storeIQ11IsBrowser(IsBrowser);
        storeIQ11ForOnboarding(IQ11ForOnboarding);
      },

      clearIQ11Token: () => {
        clearIQ11Token();
        clearIQ11TokenInfo();
        clearIQ11IsBrowser();
        clearIQ11ForOnboarding();
      },
    },
    bank: {
      getBank: () => ({ bankInfo, bankType, subBanks, accountType, mainBankInfo, bankAccountRail }),
      storeBank: (response: vp.FinancialInstitutionInfo) => {
        storeBankInfo(response);
      },
      storeMainBank: (response: vp.FinancialInstitutionInfo) => {
        storeMainBankInfo(response);
      },
      storeBankSubBanks: (response: vp.FinancialInstitutionInfo[]) => {
        storeSubBanks(response);
      },
      storeBankType: (type: 'online' | 'manual' | undefined) => {
        storeBankType(type);
      },
      storeAccountType: (type: 'personal' | 'business' | undefined) => {
        storeAccountType(type);
      },
      storeBankAccountRail: (rail: vp.BankingPaymentRail) => {
        storeBankAccountRail(rail);
      },
      clearBank: () => {
        clearBankInfo();
        clearBankType();
        clearSubBanks();
        clearAccountType();
        clearMainBankInfo();
        clearBankAccountRail();
      },
    },
    paylinkToken: {
      getPaylinkToken: () => ({ Token }),
      storePaylinkToken: (response: string) => {
        storePaylinkToken(response);
      },
      clearPaylinkToken: () => {
        clearPaylinkToken();
      },
    },
    padAgreement: {
      getPadInfo: () => ({ padInfo }),
      storePadInfo: (response: string) => {
        storePadInfo(response);
      },
      clearPadInfo: () => {
        clearPadInfo();
      },
    },
    paymentMethod: {
      getPaymentMethod: () => ({ paymentMethod }),
      storePaymentMethod: (response: string) => {
        storePaymentMethod(response);
      },
      clearPaymentMethod: () => {
        clearPaymentMethod();
      },
    },
    accountVerificationToken: {
      getAccountVerificationToken: () => ({ accountVerificationToken }),
      storeAccountVerificationToken: (response: string) => {
        storeAccountVerificationToken(response);
      },
      clearAccountVerificationToken: () => {
        clearAccountVerificationToken();
      },
    },
    accessToken: {
      getAccessTokenFields: () => ({
        accessToken: accessToken || '',
        processor: processor || '',
        paymentMethod: paymentMethod || '',
        extraInfo: accessTokenExtraInfo || {},
      }),
      storeAccessToken: (token: string) => {
        storeAccessToken(token);
      },
      storeProcessor: (processorName: string) => {
        storeProcessor(processorName);
      },
      storeAccessTokenExtraInfo: (extraInfo: vp.AccessTokenExtraInfo) => {
        storeAccessTokenExtraInfo(extraInfo);
      },
      clearAccessTokenFields: () => {
        clearAccessToken();
        clearProcessor();
        clearAccessTokenExtraInfo();
      },
    },
  };
};

function wrapSessionStorage<T extends string>(key: string): () => [T | '', (input: T) => void, () => void] {
  return () => {
    const [value, setValue, clearValue] = useSessionStorage<T | ''>(key, '');
    return [value || '', (v: T) => setValue(v), () => clearValue()];
  };
}

export default useStorage;

type UseStorageTuple<T> = [T | undefined, (value: T | undefined) => void, () => void];

function useSessionStorage<T>(key: string, _value?: T): UseStorageTuple<T> {
  return useStorageWrapper(key, _value, window.sessionStorage);
}

function useStorageWrapper<T>(key: string, _value: T, storageObject: Storage): UseStorageTuple<T> {
  const [value, setValue] = useState<T | undefined>(() => {
    const item = storageObject.getItem(key);
    return item ? (parseStoredStoredItem(item) as T) : _value;
  });

  const remove = useCallback(() => {
    storageObject.removeItem(key);
    setValue(undefined);
  }, []);

  return [
    value,
    (_value) => {
      _value !== null && _value !== undefined
        ? storageObject.setItem(key, parseNewItem(_value))
        : storageObject.removeItem(key);
      setValue(_value);
    },
    remove,
  ];

  function parseNewItem<U>(item: U | undefined): string {
    return typeof item === 'string' ? item : JSON.stringify(item);
  }

  function parseStoredStoredItem<V>(item: string | null) {
    try {
      return JSON.parse(item || '') as V;
    } catch {
      return item;
    }
  }
}
